/**
 * ContactPage  — /contact
 *
 * Public contact form. Submits to POST /api/contact which forwards
 * the message to hello@momentsent.com and sends an auto-reply.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import LandingNav from '../components/LandingNav';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';

const SUBJECTS = [
  'General Inquiry',
  'Billing & Subscription',
  'Card or Delivery Issue',
  'Account Help',
  'Press & Partnerships',
  'Feedback & Suggestions',
  'Other',
];

export default function ContactPage() {
  const [form, setForm]       = useState({ name: '', email: '', subject: SUBJECTS[0], message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState('');

  function set(field: string, value: string) {
    setForm(f => ({ ...f, [field]: value }));
    setError('');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await apiFetch('/api/contact', { method: 'POST', body: JSON.stringify(form) });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream">
      <PageSEO
        title="Contact Us | MomentSent"
        description="Have a question about MomentSent? Get in touch with our team and we will get back to you within one business day."
        canonical="https://momentsent.com/contact"
      />

      <LandingNav />

      <div className="max-w-5xl mx-auto px-6 pt-28 pb-16 lg:pt-32 lg:pb-24">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">

          {/* ── Left column — info ───────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <p className="font-body text-xs font-semibold text-sage uppercase tracking-widest mb-3">
                Get in touch
              </p>
              <h1 className="font-heading text-4xl text-navy leading-tight mb-4">
                We'd love to hear from you
              </h1>
              <p className="font-body text-gray-500 leading-relaxed">
                Questions about your account, a card that didn't arrive, or just want to say hi.
                We're a small team and we read every message.
              </p>
            </div>

            {/* Direct email */}
            <div className="bg-white rounded-2xl p-6 shadow-warm">
              <p className="font-body text-xs font-semibold text-sage uppercase tracking-widest mb-3">
                Email us directly
              </p>
              <a
                href="mailto:hello@momentsent.com"
                className="font-heading text-lg text-navy hover:text-sage transition-colors"
              >
                hello@momentsent.com
              </a>
              <p className="font-body text-sm text-gray-400 mt-1">
                We typically respond within one business day.
              </p>
            </div>

            {/* What we can help with */}
            <div className="space-y-3">
              {[
                { icon: '📬', label: 'Card & delivery issues' },
                { icon: '💳', label: 'Billing & subscription questions' },
                { icon: '🔑', label: 'Account access & settings' },
                { icon: '💡', label: 'Feature requests & feedback' },
                { icon: '🤝', label: 'Press & partnership inquiries' },
              ].map(({ icon, label }) => (
                <div key={label} className="flex items-center gap-3">
                  <span className="text-lg">{icon}</span>
                  <span className="font-body text-sm text-gray-600">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right column — form ──────────────────────────────────────── */}
          <div className="lg:col-span-3">
            {success ? (
              <SuccessState onReset={() => { setSuccess(false); setForm({ name: '', email: '', subject: SUBJECTS[0], message: '' }); }} />
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl shadow-warm p-8 space-y-5"
              >
                <h2 className="font-heading text-2xl text-navy mb-1">Send us a message</h2>
                <p className="font-body text-sm text-gray-400 !mt-1 mb-5">
                  All fields marked with * are required.
                </p>

                {/* Name + Email row */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => set('name', e.target.value)}
                      placeholder="Your name"
                      className="input-field"
                      required
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={e => set('email', e.target.value)}
                      placeholder="you@example.com"
                      className="input-field"
                      required
                      maxLength={200}
                    />
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5">
                    Subject
                  </label>
                  <select
                    value={form.subject}
                    onChange={e => set('subject', e.target.value)}
                    className="input-field"
                  >
                    {SUBJECTS.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5">
                    Message *
                  </label>
                  <textarea
                    value={form.message}
                    onChange={e => set('message', e.target.value)}
                    placeholder="Tell us what's on your mind…"
                    rows={6}
                    maxLength={4000}
                    required
                    className="input-field resize-none"
                  />
                  <p className="font-body text-xs text-gray-300 mt-1 text-right">
                    {form.message.length} / 4000
                  </p>
                </div>

                {/* Error */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    <p className="font-body text-sm text-red-600">{error}</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending…' : 'Send Message'}
                </button>

                <p className="font-body text-xs text-gray-400 text-center">
                  Or email us directly at{' '}
                  <a href="mailto:hello@momentsent.com" className="text-sage hover:underline">
                    hello@momentsent.com
                  </a>
                </p>
              </form>
            )}
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}

// ── Success state ─────────────────────────────────────────────────────────────

function SuccessState({ onReset }: { onReset: () => void }) {
  return (
    <div className="bg-white rounded-2xl shadow-warm p-10 text-center space-y-5">
      {/* Checkmark */}
      <div className="w-16 h-16 rounded-full bg-sage/10 flex items-center justify-center mx-auto">
        <svg className="w-8 h-8 text-sage" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h2 className="font-heading text-2xl text-navy">Message sent!</h2>
      <p className="font-body text-gray-500 max-w-xs mx-auto leading-relaxed">
        Thanks for reaching out. We've also sent a confirmation to your email and will be in touch within one business day.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
        <button
          onClick={onReset}
          className="btn-secondary text-sm"
        >
          Send another message
        </button>
        <Link to="/" className="btn-primary text-sm">
          Back to home
        </Link>
      </div>
    </div>
  );
}
